import React, { useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { SponsorRelatedContentBannerQuery } from "core/queries";
import { useQuery } from "@apollo/client";
import {
  createSponsorImpression,
  createSponsorClick,
  trackAdPixel,
} from "core/utils/tracker";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useInView } from "react-intersection-observer";
import { getThumbnailSrcSet } from "core/utils/getSrcSet";
import { nanoid } from "nanoid";

const StyledImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  margin: 0,
});

const StyledButtonBase = styled(ButtonBase)(() => ({
  display: "block",
  width: "100%",
}));

const StyledRoot = styled(Box)({
  display: "block",
  width: "100%",
});

const StyledAspectRatio = styled("div")({
  width: "100%",
  aspectRatio: "16 / 9",
  overflow: "hidden",
});

const VIDEO = "VIDEO";
const STREAM = "STREAM";
const CHANNEL = "CHANNEL";
const IMPRESSION = "IMPRESSION";
const DEFAULT = "DEFAULT";
const MEDIUM = "MEDIUM";
const HIGH = "HIGH";
const MAXRES = "MAXRES";

function findImageUrl(formats, { smUp, mdUp, lgUp }) {
  if (!formats?.length) {
    return null;
  }

  const indexByFormat = formats.reduce((all, item) => {
    all[item.format] = item.url;
    return all;
  }, {});

  if (lgUp && indexByFormat[MAXRES]) {
    return indexByFormat[MAXRES];
  }

  if (mdUp && indexByFormat[HIGH]) {
    return indexByFormat[HIGH];
  }

  if (smUp && indexByFormat[MEDIUM]) {
    return indexByFormat[MEDIUM];
  }

  if (indexByFormat[DEFAULT]) {
    return indexByFormat[DEFAULT];
  }

  return indexByFormat[MAXRES] || null;
}

export default function SponsorRelatedContentBanner({
  place,
  contentId,
  rightholderId,
  onNotFound,
  onReady,
}) {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const impressionSentRef = useRef(false);

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const { data, loading } = useQuery(SponsorRelatedContentBannerQuery, {
    skip: !process.browser || !rightholderId,
    fetchPolicy: "network-only",
    variables: {
      contentId,
      rightholderId,
      relatedContentBannerPlace: place,
    },
  });

  const banner = data?.sponsorCampaign?.relatedContentBanner;

  const src = banner
    ? findImageUrl(banner.thumbnails, { smUp, mdUp, lgUp })
    : null;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (banner && src) {
      onReady?.();
    } else {
      onNotFound?.();
    }
  }, [loading, banner, src, onReady, onNotFound]);

  useEffect(() => {
    if (!inView || !banner || impressionSentRef.current) {
      return;
    }

    impressionSentRef.current = true;

    const { token, impressionPixel, additionalImpressionPixels } = banner;

    createSponsorImpression(token, "RelatedContentBanner", IMPRESSION);

    trackAdPixel(impressionPixel, additionalImpressionPixels);
  }, [inView, banner]);

  if (loading || !banner || !src) {
    return null;
  }

  const {
    thumbnails,
    link,
    token,
    isClickable,
    clickPixel,
    additionalClickPixels,
  } = banner;

  const srcSet = thumbnails?.length
    ? getThumbnailSrcSet(thumbnails)
    : undefined;
  const isInteractive = isClickable && link;

  const onClick = async (event) => {
    event.preventDefault();

    if (!isInteractive) {
      return;
    }

    trackAdPixel(clickPixel, additionalClickPixels);

    const clickId = nanoid();
    const url = link.replace("{clickId}", clickId);

    window.open(url, "_blank");

    await createSponsorClick(token, clickId);
  };

  const image = (
    <StyledAspectRatio>
      <StyledImage src={src} srcSet={srcSet} alt={place} />
    </StyledAspectRatio>
  );

  if (isInteractive) {
    return (
      <StyledButtonBase ref={ref} onClick={onClick}>
        {image}
      </StyledButtonBase>
    );
  }

  return <StyledRoot ref={ref}>{image}</StyledRoot>;
}

SponsorRelatedContentBanner.propTypes = {
  place: PropTypes.oneOf([VIDEO, STREAM, CHANNEL]).isRequired,
  rightholderId: PropTypes.string.isRequired,
  contentId: PropTypes.string.isRequired,
  onNotFound: PropTypes.func,
  onReady: PropTypes.func,
};
